import { BaseStore } from 'fluxible/addons';
import normalizeUrl from '../util/normalizeUrl';

class NavigationStore extends BaseStore {
  constructor(dispatcher) {
    super(dispatcher);
    this.navigation = null;
    this.cards = null;
    this.glossary = {};
    this.breadcrumbs = [];
    this.sidebarBreadcrumbs = [];
  }

  getSections() {
    if (this.navigation) {
      return this.navigation.sections;
    }
    return [];
  }

  getMetadata(url) {
    if (this.navigation) {
      return this.navigation.metadata[normalizeUrl(url)];
    }
    return undefined;
  }

  getSidebarArticles(section, breadcrumbs) {
    if (!this.navigation) {
      return [];
    }
    if (section !== 'libraries') {
      return this.navigation.sidebar[section];
    }

    // Build library sidebar
    let sidebarArticles = [];
    // The 2nd item in breadcrumbs is the child this article belongs to
    const libraryPath = `/docs/${breadcrumbs[1].path}`;
    const libraryArticles = this.navigation.sidebar[section];
    // Filters out the rest of the library articles
    sidebarArticles = libraryArticles.filter(a => a.url === libraryPath);

    if (!sidebarArticles.length) {
      // if it doesnt exist in the sidebar.yml, add itself alone to the sidebar
      sidebarArticles = [breadcrumbs[breadcrumbs.length - 1]];
    }

    return sidebarArticles;
  }

  getBreadcrumbs() {
    return this.breadcrumbs;
  }

  getSidebarBreadcrumbs() {
    return this.sidebarBreadcrumbs;
  }

  getCards() {
    if (this.cards) {
      return this.cards;
    }
    return undefined;
  }

  getGlossary() {
    return this.glossary;
  }

  handleArticleLoaded({ url, doc }) {
    if (!doc || !doc.meta) return;
    this.setupArticleNav({ url, doc });
    this.emitChange();
  }

  setupArticleNav({ url, doc }) {
    const sideBarArticles = this.navigation.sidebar[doc.meta.section];

    const markActiveItems = (article) => {
      /* eslint-disable no-param-reassign */
      if (article.url === url) {
        article.isActiveUrl = true;
        return true;
      } else if (Array.isArray(article.children)) {
        const found = article.children.find(markActiveItems);
        if (found) {
          article.isActiveUrl = true;
          return true;
        }
      }

      article.isActiveUrl = false;
      return false;
    };

    // Mark which Articles are Active in Navigation
    sideBarArticles.forEach(markActiveItems);

    // Update Breadcrumbs in navigation
    this.breadcrumbs = [{ title: 'Docs', url: '/docs' }, ...doc.meta.breadcrumbs];
  }

  handleNavigationLoaded(payload) {
    this.navigation = {
      sections: payload.sections,
      sidebar: payload.sidebar
    };
    this.emitChange();
  }

  handleCardsLoaded(payload) {
    this.cards = payload.cards;
    this.emitChange();
  }

  handleGlossaryLoaded(payload) {
    this.glossary = payload.glossary;
    this.emitChange();
  }

  dehydrate() {
    return {
      navigation: this.navigation,
      cards: this.cards,
      glossary: this.glossary,
      breadcrumbs: this.breadcrumbs,
      sidebarBreadcrumbs: this.sidebarBreadcrumbs
    };
  }

  rehydrate(state) {
    this.navigation = state.navigation;
    this.cards = state.cards;
    this.glossary = state.glossary;
    this.breadcrumbs = state.breadcrumbs;
    this.sidebarBreadcrumbs = state.sidebarBreadcrumbs;
  }
}

NavigationStore.storeName = 'NavigationStore';
NavigationStore.handlers = {
  NAVIGATION_LOAD_SUCCESS: 'handleNavigationLoaded',
  CARDS_LOAD_SUCCESS: 'handleCardsLoaded',
  GLOSSARY_LOAD_SUCCESS: 'handleGlossaryLoaded',
  ARTICLE_LOAD_SUCCESS: 'handleArticleLoaded'
};

export default NavigationStore;
